/**
 * Message Sender Module for InterPals Private Messages
 * Handles message composition, validation, and sending with photo attachments.
 * Uses optimistic UI: text messages appear instantly before server confirmation.
 */

import {createTextCompositionTracker} from '../modules/text-composition-tracker.js';
import {trackEvent} from '../modules/ga-event.js';

// Helper to get translation (falls back to key if i18n not available)
function t(key) {
    return window.t ? window.t(key) : key;
}

function normalizeOptimisticMessageWhitespace(content) {
  if (!content) {
    return '';
  }

  return content
    .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>')
    .replace(/(\r\n|\r|\n){3,}/g, '\n\n')
    .replace(/(<br\s*\/?>\s*(\r\n|\r|\n)\s*){2,}/gi, '<br><br>')
    .replace(/((\r\n|\r|\n)\s*<br\s*\/?>\s*){2,}/gi, '<br><br>');
}

// Configuration
const MessageSenderConfig = {
  SCROLL_DELAY: 200,
  FOCUS_DELAY: 200,
  SUCCESS_MESSAGE_DELAY: 1000,
  ENDPOINT: '/app/messages/send-message'
};

// Error messages (use getters for lazy translation loading)
const MessageSenderErrors = {
  get SEND_FAILED() { return t('common.msg_send_failed'); },
  get NETWORK_ERROR() { return t('common.msg_network_error'); },
  get INVALID_THREAD() { return t('common.msg_invalid_thread'); },
  get SEND_IN_PROGRESS() { return t('common.msg_send_in_progress'); }
};

/**
 * Message data validator
 */
class MessageDataValidator {
  static validate(messageData) {
    const errors = [];

    // Must have either text or photo
    if (!messageData.hasText && !messageData.hasPhoto) {
      return errors; // Return empty array - this is handled by UI
    }

    // Thread ID must be numeric
    if (!$.isNumeric(messageData.threadId)) {
      errors.push(MessageSenderErrors.INVALID_THREAD);
    }

    return errors;
  }
}

/**
 * Message sending service
 */
class MessageSendingService {
  static async sendMessage(threadId, msgText, photoId, replyToId, textComposition = null) {
    const data = {
      "thread": threadId,
      "message": msgText
    };

    if (photoId) {
      data.photo_id = String(photoId);
    }

    if (replyToId) {
      data.reply_to_id = String(replyToId);
    }

    if (typeof textComposition === 'string') {
      data.text_composition = textComposition;
    }

    return new Promise((resolve, reject) => {
      // Set ajax request marker
      window.pm_ajax_request_active = 1;

      $.ajax({
        type: "POST",
        url: MessageSenderConfig.ENDPOINT,
        data: data,
        dataType: "json"
      }).done((response) => {
        // Auth/CSRF/onboarding gate returns {redirect: url} for a lapsed
        // session. The global ajaxComplete handler navigates, so neither
        // resolve nor reject here to avoid flashing a send-failure overlay on
        // the way out.
        if (response && typeof response.redirect === 'string' && response.redirect) {
          return;
        }
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || MessageSenderErrors.SEND_FAILED));
        }
      }).fail((xhr, status, error) => {
        console.error('Send failed:', status, error, xhr.responseText);
        reject(new Error(`${MessageSenderErrors.NETWORK_ERROR}: ${error}`));
      }).always(() => {
        window.pm_ajax_request_active = 0;
      });
    });
  }
}

/**
 * UI state manager for send operations
 */
class SendUIManager {
  constructor() {
    this.elements = {
      submitBtn: $("#msg_submit"),
      messageInput: $("#message"),
      sendButtonText: $("#send_button_text")
    };
  }

  setSendingState() {
    this.elements.submitBtn.prop("disabled", true);
    this.elements.sendButtonText.text(t('common.sending'));
    this.elements.submitBtn.attr('aria-label', t('common.sending_message'));
  }

  setReadyState() {
    this.elements.submitBtn.prop("disabled", false);
    this.elements.sendButtonText.text(t('common.send'));
    this.elements.submitBtn.attr('aria-label', t('common.send_message'));
  }
}

/**
 * Message display manager
 */
class MessageDisplayManager {
  static appendMessage(messageBody, threadsRight) {
    // Use existing append_msgs function which handles everything including scrolling
    window.append_msgs(messageBody, threadsRight);
  }

  static scrollToBottom(threadsRight) {
    setTimeout(() => {
      if (typeof threadsRight.scrollTo === "function" && threadsRight.length) {
        threadsRight.scrollTo({
          top: threadsRight.get(0).scrollHeight,
          left: 0,
          behavior: "smooth"
        });
      }
    }, MessageSenderConfig.SCROLL_DELAY);
  }

  static focusMessageBox() {
    // Skip auto-focus on mobile to avoid reopening the keyboard
    if (!window.matchMedia('(min-width: 640px)').matches) return;
    setTimeout(() => {
      $("#message").focus();
    }, MessageSenderConfig.FOCUS_DELAY);
  }

  /**
   * Move a thread to the top of the thread list immediately
   */
  static moveThreadToTop(threadId) {
    const $thread = $(`#thread_${threadId}`);
    const $threadsList = $('#threads_left');
    if ($thread.length && $threadsList.length && !$thread.is($threadsList.children().first())) {
      $threadsList.prepend($thread);
    }
  }

  /**
   * Update the thread sidebar entry with server-rendered HTML
   */
  static updateThreadSidebar(response) {
    if (response.updated_thread && response.thread_id) {
      const $existing = $(`#thread_${response.thread_id}`);
      if ($existing.length) {
        $existing.replaceWith(response.updated_thread);
        const $thread = $(`#thread_${response.thread_id}`);
        // Re-mark the thread as active
        $thread.addClass('active');
        // Move to top of thread list
        const $threadsList = $('#threads_left');
        if ($threadsList.length && !$thread.is($threadsList.children().first())) {
          $threadsList.prepend($thread);
        }
        // Re-initialize thread action buttons (desktop dropdown vs mobile swipe)
        if (window.initializeThreadActions) {
          window.initializeThreadActions();
        }
        // Initialize Lucide icons in the replaced thread
        window.LucideIcons?.initialize($thread[0]);
      }
    }
  }
}

/**
 * Builds optimistic (placeholder) message HTML for instant display
 */
class OptimisticMessageBuilder {
  static _nextId = 0;

  /**
   * Get current user info from the page
   */
  static getCurrentUserInfo() {
    const username = $('meta[name="username"]').attr('content') || '';
    const avatarImg = $('img.sidebar-user-avatar');
    const thumbUrl = avatarImg.length ? avatarImg.attr('src') : '';
    return { username, thumbUrl };
  }

  /**
   * Generate a temporary unique ID for optimistic messages
   */
  static generateTempId() {
    return `optimistic_${Date.now()}_${++OptimisticMessageBuilder._nextId}`;
  }

  static getOptimisticTopMarginClass(userId) {
    const wrapper = document.getElementById('messages_wrapper');
    if (!wrapper) {
      return 'mt-3';
    }

    const children = Array.from(wrapper.children);
    for (let i = children.length - 1; i >= 0; i--) {
      const child = children[i];
      if (!child.classList?.contains('pm_msg')) {
        if (child.classList?.contains('pm_date_div')) {
          return 'mt-3';
        }
        continue;
      }

      const senderId = child.getAttribute('data-sender-id');
      return senderId === String(userId) ? 'mt-1.5' : 'mt-3';
    }

    return 'mt-3';
  }

  /**
   * Escape HTML to prevent XSS in optimistic message display
   */
  static escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Convert plain text to display HTML (line breaks)
   */
  static textToHtml(text) {
    return OptimisticMessageBuilder.escapeHtml(text).replace(/\n/g, '<br>');
  }

  static formatTextHtml(text) {
    const editorBase = window.editorBase;
    if (editorBase && typeof editorBase.formatWithBBCode === 'function') {
      return normalizeOptimisticMessageWhitespace(editorBase.formatWithBBCode(text));
    }

    return OptimisticMessageBuilder.textToHtml(text);
  }

  /**
   * Check if text consists solely of emoji characters (max ~8 emoji).
   */
  static isEmojiOnly(text) {
    const stripped = text.replace(/\s/g, '');
    if (!stripped) return false;
    const emojiPattern = /^(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base}\p{Emoji_Modifier}?|\p{Regional_Indicator}{2}|\u200D)+$/u;
    return emojiPattern.test(stripped) && [...stripped].length <= 12;
  }

  /**
   * Build HTML for an optimistic text message
   */
  static buildTextMessage(msgText, tempId) {
    const { username } = OptimisticMessageBuilder.getCurrentUserInfo();
    const userId = window.pmCurrentUserId();
    const topMarginClass = OptimisticMessageBuilder.getOptimisticTopMarginClass(userId);
    const now = new Date();
    const time = now.getHours() + ':' + String(now.getMinutes()).padStart(2, '0');
    const htmlContent = OptimisticMessageBuilder.formatTextHtml(msgText);
    const escapedUsername = OptimisticMessageBuilder.escapeHtml(username);
    const escapedPreview = OptimisticMessageBuilder.escapeHtml(msgText.substring(0, 100));
    const emojiOnly = OptimisticMessageBuilder.isEmojiOnly(msgText);
    const bubbleExtraClasses = emojiOnly ? ' msg_body--no-bubble msg_body--emoji-only' : '';
    const timeEsc = OptimisticMessageBuilder.escapeHtml(time);
    // Emoji-only messages have no bubble, so they keep the below-bubble meta row.
    // Normal text renders the time in the bubble corner with a wrap-around reserve.
    const reserve = emojiOnly ? '' : `<span class="pm-time-reserve" aria-hidden="true">${timeEsc}<span class="pm-time-reserve-check"></span></span>`;
    const cornerTime = emojiOnly ? '' : `
              <span class="pm-time">
                <span class="pm-time-text">${timeEsc}</span>
                <span class="read-receipt-wrap flex items-center" data-status="unread">
                  <i data-lucide="check" class="read-receipt w-3.5 h-3.5 text-gray-400"></i>
                </span>
              </span>`;
    const belowMeta = emojiOnly ? `
            <div class="pm-msg-meta flex items-center gap-1 mt-0.5 justify-end pr-1">
              <span class="pm-msg-time text-[11px] leading-none text-gray-400 select-none">${timeEsc}</span>
              <span class="read-receipt-wrap flex items-center" data-status="unread">
                <i data-lucide="check" class="read-receipt w-3.5 h-3.5 text-gray-400"></i>
              </span>
            </div>` : '';

    return `
      <div class="pm_msg own_msg ${topMarginClass} flex w-full overflow-x-hidden group optimistic-msg justify-end"
           id="msg_${tempId}"
           data-sender-id="${OptimisticMessageBuilder.escapeHtml(userId)}"
           data-sender-name="${escapedUsername}"
           data-msg-preview="${escapedPreview}"
           data-optimistic="true">
        <div class="flex flex-col items-end w-full max-w-full">
          <div class="min-w-0 max-w-full w-full flex flex-col items-end">
            <div class="pm-msg-bubble-wrap relative w-fit max-w-[85vw] sm:max-w-[80%]">
              <div class="break-words rounded-lg px-3 py-1.5 text-base msg_body text-wrap msg_body--own optimistic-msg-body${bubbleExtraClasses}">
                ${htmlContent}${reserve}
              </div>${cornerTime}
              <div class="pm-msg-toolbar msg-inline-actions msg-hover-action z-10 hidden sm:flex items-center sm:opacity-0 sm:group-hover:opacity-100 transition-opacity pointer-events-none"
                   aria-hidden="true">
                <div class="flex items-center gap-0.5 rounded-full bg-white dark:bg-gray-800 shadow-md ring-1 ring-gray-200 dark:ring-gray-700 px-2 py-1 [&_svg]:w-[18px] [&_svg]:h-[18px]"
                     title="${OptimisticMessageBuilder.escapeHtml(time)}">
                  <button type="button" tabindex="-1" class="pointer-events-none">
                    <i data-lucide="reply"></i>
                  </button>
                  <button type="button" tabindex="-1" class="msg-actions-toggle pointer-events-none">
                    <i data-lucide="ellipsis"></i>
                  </button>
                </div>
              </div>
            </div>${belowMeta}
          </div>
        </div>
      </div>`;
  }

  /**
   * Build error state HTML for a failed optimistic message
   */
  static buildErrorOverlay(tempId, errorMsg = null) {
    const displayMsg = errorMsg || t('common.msg_send_failed');
    return `<div class="flex items-center gap-2 mt-1 text-sm text-red-500 optimistic-error" data-temp-id="${tempId}">
      <i class="fa fa-exclamation-circle"></i>
      <span>${displayMsg}</span>
      <button type="button" class="underline hover:text-red-700 optimistic-retry-btn"
              data-temp-id="${tempId}">${t('common.retry')}</button>
      <button type="button" class="underline hover:text-red-700 optimistic-dismiss-btn"
              data-temp-id="${tempId}">${t('common.remove')}</button>
    </div>`;
  }
}

/**
 * Main message sender class
 */
class MessageSender {
  constructor() {
    this.isSending = false;
    this.uiManager = new SendUIManager();
    this._failedMessages = new Map();
    const messageInput = document.getElementById('message');
    const initialValue = String(messageInput?.value ?? '');
    this.compositionTracker = createTextCompositionTracker({
      initialValue,
      baseline: initialValue ? 'restored_draft' : 'empty'
    });
    if (messageInput) {
      this.compositionTracker.attach(messageInput);
    }
    this.compositionSubmissionAttempt = null;
    this._bindErrorHandlers();
  }

  /**
   * Bind click handlers for retry/dismiss on failed optimistic messages
   */
  _bindErrorHandlers() {
    const threadsRight = $("#threads_right");

    threadsRight.on('click', '.optimistic-retry-btn', (e) => {
      const tempId = $(e.currentTarget).data('temp-id');
      const attempt = this._failedMessages.get(tempId);
      if (!attempt) return;

      // Remove the error overlay and reset message opacity
      $(e.currentTarget).closest('.optimistic-error').remove();
      $(`#msg_${tempId}`).removeClass('optimistic-failed');
      this._failedMessages.delete(tempId);

      this._retrySend(tempId, attempt);
    });

    threadsRight.on('click', '.optimistic-dismiss-btn', (e) => {
      const tempId = $(e.currentTarget).data('temp-id');
      $(`#msg_${tempId}`).remove();
      $(e.currentTarget).closest('.optimistic-error').remove();
      this._failedMessages.delete(tempId);
    });
  }

  /**
   * Retry sending a failed optimistic message
   */
  async _retrySend(tempId, attempt) {
    try {
      const response = await MessageSendingService.sendMessage(
        attempt.threadId,
        attempt.msgText,
        null,
        attempt.replyToId,
        attempt.textComposition
      );
      // Replace optimistic message with server-rendered version
      const $serverMsg = $(response.body);
      $(`#msg_${tempId}`).replaceWith($serverMsg);
      $serverMsg.each(function() { window.LucideIcons?.initialize(this); });
      MessageDisplayManager.updateThreadSidebar(response);
    } catch (error) {
      this._markOptimisticFailed(tempId, attempt, error.message);
    }
  }

  /**
   * Mark an optimistic message as failed and show retry/dismiss
   */
  _markOptimisticFailed(tempId, attempt, errorMsg = null) {
    const $msg = $(`#msg_${tempId}`);
    $msg.addClass('optimistic-failed');
    this._failedMessages.set(tempId, attempt);
    const errorHtml = OptimisticMessageBuilder.buildErrorOverlay(tempId, errorMsg);
    const $error = $(errorHtml);
    $msg.after($error);
    $error[0]?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }

  /**
   * Main entry point for sending messages
   */
  async sendMessage() {
    const messageData = this.gatherMessageData();

    // Early return if no content (replies require text, but allow the send flow)
    if (!messageData.hasText && !messageData.hasPhoto) {
      return false;
    }

    // Validate message data
    const validationErrors = MessageDataValidator.validate(messageData);
    if (validationErrors.length > 0) {
      this.showError(validationErrors[0]);
      return false;
    }

    // For text-only messages, use optimistic UI: show message immediately, clear input, send in background
    if (messageData.hasText && !messageData.hasPhoto) {
      this._sendTextOptimistic(messageData);
    } else {
      // Photo messages still use the sequential flow (need upload progress)
      if (this.isSending) {
        return false;
      }
      this.isSending = true;
      this.uiManager.setSendingState();
      try {
        await this.sendAllMessages(messageData);
        this.handleSendSuccess(messageData);
      } catch (error) {
        this.handleSendError(error);
      } finally {
        this.isSending = false;
        this.uiManager.setReadyState();
      }
    }

    return false; // Prevent form submission
  }

  /**
   * Optimistic send for text messages: show immediately, send in background
   */
  _sendTextOptimistic(messageData) {
    const { threadId, msgText, threadsRight, replyToId, textComposition } = messageData;
    const tempId = OptimisticMessageBuilder.generateTempId();
    const attempt = Object.freeze({threadId, msgText, replyToId, textComposition});

    // Show optimistic message immediately
    const optimisticHtml = OptimisticMessageBuilder.buildTextMessage(msgText, tempId);
    MessageDisplayManager.appendMessage(optimisticHtml, threadsRight);

    // Move thread to top of list immediately
    MessageDisplayManager.moveThreadToTop(threadId);

    // Clear input and UI state immediately
    this._clearInputState(messageData);

    // Send to server in the background
    MessageSendingService.sendMessage(threadId, msgText, null, replyToId, textComposition)
      .then((response) => {
        trackEvent('message_sent');
        // Replace optimistic placeholder with server-rendered message
        const $optimistic = $(`#msg_${tempId}`);
        if ($optimistic.length) {
          const preservedMarginClass = ($optimistic.attr('class') || '').split(/\s+/)
            .find((className) => /^mt-/.test(className));
          const $serverMsg = $(response.body);
          if (preservedMarginClass) {
            const $serverPmMsg = $serverMsg.filter('.pm_msg').first().length
              ? $serverMsg.filter('.pm_msg').first()
              : $serverMsg.find('.pm_msg').first();
            if ($serverPmMsg.length) {
              const existingMarginClasses = ($serverPmMsg.attr('class') || '').split(/\s+/)
                .filter((className) => !/^mt-/.test(className));
              $serverPmMsg.attr('class', [...existingMarginClasses, preservedMarginClass].join(' ').trim());
            }
          }
          $optimistic.replaceWith($serverMsg);
          $serverMsg.each(function() { if (this.nodeType === 1) window.LucideIcons?.initialize(this); });
        }
        // Update thread sidebar
        MessageDisplayManager.updateThreadSidebar(response);
      })
      .catch((error) => {
        console.error('Error sending message:', error);
        this._markOptimisticFailed(tempId, attempt, error.message);
      });
  }

  /**
   * Clear input fields and UI state after sending
   */
  _clearInputState(messageData) {
    // Clear draft
    if (typeof last_draft !== 'undefined' && typeof last_draft[messageData.threadId] !== "undefined") {
      delete last_draft[messageData.threadId];
    }
    window.draftStorage?.remove(messageData.threadId);

    // Clear message input and reset textarea height
    messageData.msgBlock.val("");
    this._rebaseComposition('', 'empty');
    messageData.msgBlock.css("height", "");
    messageData.msgBlock[0].style.height = 'auto';
    // Trigger input event so send button state updates
    messageData.msgBlock.trigger('input');

    // Clear reply context
    if (window.cancelReply) {
      window.cancelReply();
    }

    // Collapse textarea
    messageData.threadsMain.removeClass("send");

    // Focus message box
    MessageDisplayManager.focusMessageBox();
  }

  /**
   * Gather all necessary data for sending
   */
  gatherMessageData() {
    const msgBlock = $("#message");
    const msgText = $.trim(msgBlock.val());
    const threadId = $("#send_thread_id").val();
    const photoQueue = window.photoUpload?.getPhotoQueue() || [];
    const replyToId = $("#reply_to_id").val() || null;
    if (msgText.length > 0) {
      this.compositionSubmissionAttempt = this.compositionTracker.freezeAttempt(
        msgText,
        this.compositionSubmissionAttempt
      );
    }
    const textComposition = msgText.length > 0 ? this.compositionSubmissionAttempt.serialized : null;

    return {
      msgBlock,
      msgText,
      threadId,
      photoQueue,
      replyToId,
      textComposition,
      hasText: msgText.length > 0,
      hasPhoto: photoQueue.length > 0,
      threadsRight: $("#threads_right"),
      threadsMain: $("#threads_main")
    };
  }

  /**
   * Send text message (if any) followed by each photo as a separate message
   */
  async sendAllMessages(messageData) {
    const { threadId, msgText, photoQueue, threadsRight, replyToId, textComposition } = messageData;

    // Send text message first (if any) — reply is attached to the text message only
    if (messageData.hasText) {
      const textResponse = await MessageSendingService.sendMessage(
        threadId,
        msgText,
        null,
        replyToId,
        textComposition
      );
      MessageDisplayManager.appendMessage(textResponse.body, threadsRight);
      MessageDisplayManager.updateThreadSidebar(textResponse);

      // Clear text immediately so it won't be re-sent if a photo upload fails below
      messageData.msgBlock.val("");
      this._rebaseComposition('', 'empty');
      messageData.msgBlock.css("height", "");
      messageData.msgBlock[0].style.height = 'auto';
    }

    // Send each photo as a separate message using pre-uploaded photo IDs
    // Note: Reply is only attached to text messages. Photos are always sent without reply context.
    if (photoQueue.length > 0) {
      const allUploaded = photoQueue.every(e => e.photoId);

      // Only show progress bar if some photos are still uploading
      if (!allUploaded) {
        window.photoUpload.showProgress();
      }

      try {
        for (let i = 0; i < photoQueue.length; i++) {
          const entry = photoQueue[i];

          // Use pre-uploaded photoId, or await in-flight/retry upload
          let photoId = entry.photoId;
          if (!photoId) {
            try {
              if (entry.uploadPromise) {
                photoId = await entry.uploadPromise;
              }
            } catch {
              // Eager upload failed — retry now
              photoId = null;
            }
            if (!photoId) {
              photoId = await window.photoUpload.uploadPhoto(entry.file, (percent) => {
                const overallPercent = ((i * 100) + percent) / photoQueue.length;
                $(document).trigger('photo-upload-progress', overallPercent);
              });
            }
          }

          const photoResponse = await MessageSendingService.sendMessage(threadId, '', photoId);
          MessageDisplayManager.appendMessage(photoResponse.body, threadsRight);
          MessageDisplayManager.updateThreadSidebar(photoResponse);
        }
      } finally {
        window.photoUpload.hideProgress();
      }

      // Clear queue without deleting from server — photos are now attached to messages
      window.photoUpload.removeAllPhotos({ deleteFromServer: false });
    }
  }

  /**
   * Handle successful message sending
   */
  handleSendSuccess(messageData) {
    // Clear draft from cache and local storage
    if (typeof last_draft !== 'undefined' && typeof last_draft[messageData.threadId] !== "undefined") {
      delete last_draft[messageData.threadId];
    }
    window.draftStorage?.remove(messageData.threadId);

    // Clear message input and reset textarea height
    messageData.msgBlock.val("");
    this._rebaseComposition('', 'empty');
    messageData.msgBlock.css("height", "");
    messageData.msgBlock[0].style.height = 'auto';

    // Clear photos if present (already sent, don't delete from server)
    if (window.photoUpload?.hasPhoto()) {
      window.photoUpload.removeAllPhotos({ deleteFromServer: false });
    }

    // Clear reply after send — reply was either consumed with the text message,
    // or user chose to send without text (reply not attached but intent completed)
    if (window.cancelReply) {
      window.cancelReply();
    }

    // Collapse textarea
    messageData.threadsMain.removeClass("send");

    // Focus message box
    MessageDisplayManager.focusMessageBox();
  }

  /**
   * Handle send errors
   */
  handleSendError(error) {
    console.error('Error sending message:', error);
    this.showError(error.message || MessageSenderErrors.SEND_FAILED);
  }

  _rebaseComposition(value, baseline) {
    this.compositionTracker.setValueWithoutObservation(value, baseline);
    this.compositionSubmissionAttempt = null;
  }

  /**
   * Show error message to user
   */
  showError(message) {
    alert(message);
  }
}

// Global initialization
let messageSender;

function initMessageSender() {
  messageSender = new MessageSender();
  return messageSender;
}

window.rebaseMessageComposition = function(value, baseline = 'restored_draft') {
  if (messageSender) {
    messageSender._rebaseComposition(value, baseline);
  }
};

// Shared with pm/correction.js so a sent correction updates the thread
// sidebar the same way a regular sent message does.
window.MessageDisplayManager = MessageDisplayManager;

// Legacy global function for backward compatibility
window.send_msg = function() {
  if (!messageSender) {
    messageSender = initMessageSender();
  }
  return messageSender.sendMessage();
};

// Auto-initialize
if (typeof $ !== 'undefined') {
  $(document).ready(() => {
    if ($(".privateMessages").length > 0) {
      initMessageSender();
    }
  });
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    MessageSender,
    MessageSenderConfig,
    MessageSenderErrors,
    MessageDataValidator,
    MessageSendingService,
    MessageDisplayManager,
    SendUIManager,
    OptimisticMessageBuilder
  };
}
